// Date picker that wraps Diletta's existing CustomCalendar
// (src/components/calendar/calendar.tsx — a react-calendar wrapper used
// across the platform). The exotics page consumes this same primitive
// so the calendar chrome and behaviour match the rest of Diletta.
//
// Also accepts typed shorthand ("0DTE", "M1", "W2", "Q1", "3D", "2W", "1M")
// as a faster route than opening the calendar — see ../../../pages/
// exotics-pricer/expiry-shorthand.ts for the resolution rules. A literal
// date (dd/mm/yyyy or YYYY-MM-DD) can still be typed directly too, same as
// picking one off the calendar — only shorthand tokens are required to
// land on a real listed expiry.
//
// External API (value / onChange / ariaLabel) is unchanged so the trade
// ExpiryCell call site doesn't need to know about CustomCalendar's
// react-calendar-style props, beyond the new optional `listedExpiries`.
//
// Storage: "YYYY-MM-DD" strings (matches contracts.ts Instrument.expiry).
// Display: dd-mmm-yy (e.g. "01-Jan-26") — numeric dd/mm/yyyy reads as
// ambiguous (US vs UK day/month order) once a trader's eye is skimming a
// row of dates, so the month is spelled out instead.
import { FC, KeyboardEvent, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Popover } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';

import CustomCalendar from '../../calendar/calendar';
import { resolveExpiryShorthand } from '../../../pages/exotics-pricer/expiry-shorthand';

export interface DatePickerProps {
  value: string; // "YYYY-MM-DD" or ""
  onChange: (next: string) => void;
  ariaLabel: string;
  placeholder?: string;
  minDate?: string;
  disabled?: boolean;
  // Real listed-expiry chain for the instrument's token — shorthand tokens
  // (0DTE/M1/W1/Q1/1D/1W/1M...) must resolve to a date in this list.
  // Omit (or pass []) to disable shorthand resolution — falls back to
  // literal-date-only typing.
  listedExpiries?: string[];
  // Explicit tab stop position — see trade-cells.tsx's tabIndexFor(). Omit
  // for natural DOM-order tabbing.
  tabIndex?: number;
}

const pad2 = (n: number) => String(n).padStart(2, '0');
const fmt = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const parse = (s: string): Date | null => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const fmtDisplay = (s: string): string => {
  const d = parse(s);
  if (!d) return '';
  return `${pad2(d.getDate())}-${MONTH_ABBR[d.getMonth()]}-${String(d.getFullYear()).slice(-2)}`;
};

// Literal-date fallback for typed input that isn't shorthand: accepts the
// display format (dd-mmm-yy[yy]), the legacy numeric dd/mm/yyyy format, or
// the storage format (YYYY-MM-DD). Unlike shorthand, a literal date is
// accepted as-is (not required to be a real listed expiry) — matches
// today's calendar behaviour, which lets you pick any in-range date.
const parseLiteralDate = (raw: string): string | null => {
  const trimmed = raw.trim();
  const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const d = parse(trimmed);
    return d ? trimmed : null;
  }
  const monthMatch = trimmed.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{2}|\d{4})$/);
  if (monthMatch) {
    const [, dd, mmm, yy] = monthMatch;
    const monthIndex = MONTH_ABBR.findIndex((m) => m.toLowerCase() === mmm.toLowerCase());
    if (monthIndex === -1) return null;
    const yyyy = yy.length === 2 ? 2000 + Number(yy) : Number(yy);
    const d = new Date(yyyy, monthIndex, Number(dd));
    if (d.getFullYear() !== yyyy || d.getMonth() !== monthIndex || d.getDate() !== Number(dd)) {
      return null; // rolled over -> not a real date
    }
    return fmt(d);
  }
  const displayMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (displayMatch) {
    const [, dd, mm, yyyy] = displayMatch;
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    if (
      d.getFullYear() !== Number(yyyy) ||
      d.getMonth() !== Number(mm) - 1 ||
      d.getDate() !== Number(dd)
    ) {
      return null; // rolled over (e.g. 31/02) -> not a real date
    }
    return fmt(d);
  }
  return null;
};

// Far-future maxDate so the calendar lets users pick expiry dates.
// CustomCalendar defaults to `maxDate || new Date()` (today) which would
// disable every future day. Five years out is plenty for any exotic.
const FAR_FUTURE = new Date(Date.now() + 5 * 365 * 24 * 60 * 60 * 1000);

const DatePicker: FC<DatePickerProps> = ({
  value,
  onChange,
  ariaLabel,
  placeholder = 'dd-mmm-yy',
  minDate,
  disabled,
  listedExpiries = [],
  tabIndex,
}) => {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState('');
  const [invalid, setInvalid] = useState(false);

  // Earliest selectable expiry. A caller may pin a floor via the `minDate`
  // ("YYYY-MM-DD") prop; otherwise we default to today so past expiry dates
  // are not selectable. `parse` returns null for empty/malformed input, in
  // which case we fall back to today as well.
  const minSelectable = parse(minDate ?? '') ?? new Date();

  // Calendar anchor (its initially-shown month). Route the stored value
  // through `parse` rather than `new Date(value)` directly: a malformed but
  // truthy string (e.g. '0000-00-00') makes `new Date(...).toISOString()`
  // throw `RangeError: Invalid time value`. parse() returns null for any
  // invalid input, so we fall back to today.
  const calendarAnchor = (parse(value) ?? new Date()).toISOString();

  const displayText = useMemo(() => {
    if (focused) return draft;
    return fmtDisplay(value);
  }, [focused, draft, value]);

  const openCalendar = () => {
    if (disabled) return;
    setOpen(true);
  };

  const handleCalendarSelect = (d: Date) => {
    onChange(fmt(d));
    setInvalid(false);
    setOpen(false);
  };

  const onFocus = () => {
    if (disabled) return;
    setDraft(fmtDisplay(value));
    setInvalid(false);
    setFocused(true);
    requestAnimationFrame(() => {
      inputRef.current?.select();
    });
  };

  const tryCommit = (raw: string) => {
    if (raw.trim() === '') {
      setInvalid(false);
      return;
    }
    const resolved =
      resolveExpiryShorthand(raw, new Date(), listedExpiries) ?? parseLiteralDate(raw);
    if (resolved === null) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    onChange(resolved);
  };

  const onBlur = () => {
    tryCommit(draft);
    setFocused(false);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // Prevent default first: if this input sits inside a <form> (or any
      // ancestor with its own Enter handling), the browser's native
      // Enter behaviour (form submit) can fire before/around our manual
      // blur() below and interfere with the commit — e.g. re-focusing an
      // element and resetting state before the invalid/committed render
      // is visible.
      e.preventDefault();
      // Enter doesn't natively move focus, so force a blur to commit.
      // Tab is deliberately NOT handled here: it already blurs (moving
      // focus to the next element) as native default behaviour, and
      // calling blur() ourselves too raced against that native focus
      // change in a way that could drop the commit.
      inputRef.current?.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setInvalid(false);
      setFocused(false);
    }
  };

  return (
    <>
      <Box
        ref={anchorRef}
        sx={{
          width: '100%',
          minHeight: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          padding: '2px 6px',
          borderRadius: 0.5,
          fontFamily: 'Inter',
          fontVariantNumeric: 'tabular-nums',
          // 12px — matches Select / EditableNumber caption sizing used
          // across the Trade band so all field controls read at one size.
          fontSize: '0.75rem',
          border: '1px solid transparent',
          borderBottom: invalid ? '1px solid' : undefined,
          borderBottomColor: invalid ? 'error.main' : undefined,
          '&:hover': { borderColor: disabled ? 'transparent' : 'divider' },
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <input
          ref={inputRef}
          type="text"
          autoComplete="off"
          spellCheck={false}
          aria-label={ariaLabel}
          aria-invalid={invalid || undefined}
          disabled={disabled}
          placeholder={placeholder}
          value={displayText}
          tabIndex={tabIndex}
          onChange={(e) => {
            setDraft(e.target.value);
            if (invalid) setInvalid(false);
          }}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          style={{
            flex: 1,
            minWidth: 0,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            padding: 0,
            font: 'inherit',
            color: value || focused ? 'inherit' : 'var(--mui-palette-text-disabled, #999)',
            cursor: disabled ? 'not-allowed' : 'text',
          }}
        />
        <IconButton
          size="small"
          disabled={disabled}
          onClick={openCalendar}
          aria-label={`${ariaLabel}-open-calendar`}
          // Mouse-driven aux action — kept out of the explicit tab-down
          // sequence (see trade-cells.tsx) rather than landing at the end
          // of it via the default tabIndex=0.
          tabIndex={-1}
          sx={{ p: 0.25 }}
        >
          <FontAwesomeIcon icon={faCalendar} style={{ fontSize: 11, opacity: 0.7 }} />
        </IconButton>
      </Box>

      <Popover
        open={open && !disabled}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              // Fixed width — react-calendar needs ~310px to render its
              // month grid cleanly. The TimestampCalendarPicker uses
              // 20.5rem (~328px) for the same reason.
              width: '20.5rem',
              backgroundColor: '#002649',
              border: 1,
              borderColor: 'divider',
              p: 0.5,
            },
          },
        }}
      >
        <CustomCalendar
          value={calendarAnchor}
          handler={handleCalendarSelect}
          minDate={minSelectable}
          maxDate={FAR_FUTURE}
          newVersion
        />
      </Popover>
    </>
  );
};

export default DatePicker;
